import { FieldTree } from '@angular/forms/signals';

/**
 * Minimal structural type matching any FieldTree node for dirty-state traversal.
 * Avoids generic variance issues with FieldTree<T>.
 */
interface DirtyCheckableField {
  (): { readonly dirty: () => boolean };
  [Symbol.iterator]?(): Iterator<[string, DirtyCheckableField | undefined] | DirtyCheckableField>;
}

/**
 * Minimal structural type matching any FieldTree node for validity traversal.
 */
interface ValidatableField {
  (): {
    readonly invalid: () => boolean;
    readonly errors: () => readonly { readonly kind: string }[];
  };
  [Symbol.iterator]?(): Iterator<[string, ValidatableField | undefined] | ValidatableField>;
}

export interface InvalidField {
  readonly path: string;
  readonly errors: readonly { readonly kind: string }[];
}

export function dirtyFields(field: DirtyCheckableField, path = 'root'): string[] {
  const dirtyPaths: string[] = [];
  const state = field();

  // If this node itself is dirty, record it
  if (state.dirty()) {
    // Check if it has iterable children (group or array node)
    const iterator = field[Symbol.iterator]?.();
    if (iterator) {
      let index = 0;
      // Recurse into children to find the dirty leaves
      for (const entry of field as Iterable<[string, DirtyCheckableField | undefined] | DirtyCheckableField>) {
        if (Array.isArray(entry)) {
          // Group field: entry is [key, childField]
          const [key, child] = entry;
          if (child) {
            dirtyPaths.push(...dirtyFields(child, `${path}.${key}`));
          }
        } else if (entry) {
          // Array field: entry is a child field directly
          dirtyPaths.push(...dirtyFields(entry as DirtyCheckableField, `${path}[${index}]`));
        }
        index++;
      }
      // If no dirty children were found, this node is itself the dirty leaf
      if (dirtyPaths.length === 0) {
        dirtyPaths.push(path);
      }
    } else {
      // Leaf node that is dirty
      dirtyPaths.push(path);
    }
  }
  return dirtyPaths;
}

export function logDirtyFields(field: DirtyCheckableField, path = 'root'): void {
  const dirtyPaths = dirtyFields(field, path);
  dirtyPaths.forEach(dirtyPath => console.log(`Dirty field: ${dirtyPath}`));
}

export function invalidFields(field: ValidatableField, path = 'root'): InvalidField[] {
  const result: InvalidField[] = [];
  const state = field();

  if (state.invalid()) {
    const iterator = field[Symbol.iterator]?.();
    if (iterator) {
      let index = 0;
      for (const entry of field as Iterable<[string, ValidatableField | undefined] | ValidatableField>) {
        if (Array.isArray(entry)) {
          // Group field: entry is [key, childField]
          const [key, child] = entry;
          if (child) {
            result.push(...invalidFields(child, `${path}.${key}`));
          }
        } else if (entry) {
          // Array field: entry is a child field directly
          result.push(...invalidFields(entry as ValidatableField, `${path}[${index}]`));
        }
        index++;
      }
      if (result.length === 0) {
        result.push({ path, errors: state.errors() });
      }
    } else {
      result.push({ path, errors: state.errors() });
    }
  }
  return result;
}

export function logInvalidFields(field: ValidatableField, path = 'root'): void {
  const fields = invalidFields(field, path);
  fields.forEach(f => {
    const kinds = f.errors.map(e => e.kind).join(', ');
    console.log(`Invalid field: ${f.path} [${kinds}]`);
  });
}
